import ENV from 'env';
import { RentType, Spot } from './constants';

export type LatLng = { lat: number; lng: number };

type Located = { address: { latitude: string; longitude: string } };

function toRadians(degrees: number) {
    return (degrees * Math.PI) / 180;
}

export function distanceMiles(origin: LatLng, dest: LatLng) {
    const earthRadiusMiles = 3958.8;
    const dLat = toRadians(dest.lat - origin.lat);
    const dLng = toRadians(dest.lng - origin.lng);
    const lat1 = toRadians(origin.lat);
    const lat2 = toRadians(dest.lat);

    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return earthRadiusMiles * c;
}

export function formatDistanceMiles(origin: LatLng, dest: LatLng) {
    const miles = distanceMiles(origin, dest);
    if (miles < 0.1) {
        return `${Math.round(miles * 5280)} ft`;
    }
    if (miles < 10) {
        return `${miles.toFixed(1)} mi`;
    }
    return `${Math.round(miles)} mi`;
}

export function spotLatLng(spot: Located): LatLng {
    return { lat: parseFloat(spot.address.latitude), lng: parseFloat(spot.address.longitude) };
}

export function formatDistanceAway(origin: LatLng | undefined, spot: Located): string | null {
    if (!origin) return null;
    const dest = spotLatLng(spot);
    if (Number.isNaN(dest.lat) || Number.isNaN(dest.lng)) return null;
    return `${formatDistanceMiles(origin, dest)} away`;
}

export function formatSpotAddress(spot: Spot) {
    const { address_line1, city, state, zipcode } = spot.address;
    return `${address_line1}, ${city}, ${state} ${zipcode}`;
}

export type RateKey = 'hourly' | 'daily' | 'monthly';

export interface RateTier {
    /** How long you stay, as the rate text puts it, e.g. "0 to 1 hr" or "First Hour". */
    time: string;
    /** What you pay for that span, e.g. "Free" or "$2.00/hr". */
    price: string;
}

export interface MovingRate {
    tiers: RateTier[];
    dailyMax: string | null;
}

export interface RateOption {
    key: RateKey;
    label: string;
    price: string;
    unit: string;
    note?: string;
    /** Tiered pricing for moving rates; the headline price is the first tier. */
    moving?: MovingRate;
    /** Short range for compact spots such as the map popup, e.g. "Free – $16.00". */
    rangeLabel?: string;
    /** The rate's own explanatory copy from the backend, when set. */
    terms?: string;
    showHourlyNotice: boolean;
    bookingUrl: string;
    infoUrl: string;
}

const tidy = (text: string) => text.replace(/\s*\/\s*$/, '').trim();

function splitTier(segment: string): RateTier {
    const separator = segment.lastIndexOf(' - ') >= 0 ? segment.lastIndexOf(' - ') : segment.lastIndexOf('-');
    if (separator < 0) return { time: tidy(segment), price: '' };
    return { time: tidy(segment.slice(0, separator)), price: tidy(segment.slice(separator + 1).replace(/^-/, '')) };
}

/**
 * Parse a moving rate's tier text, e.g. "0 to 1 hr - Free&Hourly Rate - $2.00/hr&Daily Max - $16.00".
 * Same rules as QuickPay: segments split on "&", and a trailing "Daily Max" segment is pulled out.
 */
export function parseMovingRate(displayPrice?: string | null): MovingRate | null {
    if (!displayPrice) return null;
    const segments = displayPrice
        .split('&')
        .map((segment) => segment.replace(/[\r\n]+/g, ' ').trim())
        .filter(Boolean);
    let dailyMax: string | null = null;
    const last = segments[segments.length - 1];
    if (last && /daily max/i.test(last)) {
        segments.pop();
        dailyMax = splitTier(last).price || null;
    }
    const tiers = segments.map(splitTier).filter((tier) => tier.price);
    return tiers.length ? { tiers, dailyMax } : null;
}

/** "0 to 1 hr" → "for 0 to 1 hr"; "First Hour" → "first hour". */
function describeTierTime(time: string): string {
    const lower = time.toLowerCase();
    return /^\d/.test(lower) ? `for ${lower}` : lower;
}

export const formatCents = (cents: number) => `$${(cents / 100).toFixed(2)}`;

const bookingUrl = (spot: Spot, key: RateKey) =>
    `${ENV.QUICKPAY_DOMAIN}spots/${spot.uuid}/?rental_type=${key}&ref=spot-search`;

const infoUrl = (spot: Spot, key: RateKey) => `${spot.webflow_page_url}/?rentalType=${key}`;

/** Monthly parking booked through a business account (an admin manages several drivers). */
export const businessAccountBookingUrl = (spot: Spot) =>
    `${ENV.QUICKPAY_DOMAIN}business-account-parking/spots/${spot.pk}`;

/** Rate tabs a spot offers, in display order. Mirrors the availability rules of the previous card. */
export function getRateOptions(spot: Spot): RateOption[] {
    const options: RateOption[] = [];

    if (spot.display_price) {
        const isFlatRate = spot.active_rate?.unit === RentType.flatRate;
        const moving = isFlatRate ? null : parseMovingRate(spot.active_rate?.display_price);
        const terms = spot.active_rate?.subtext_override || undefined;
        if (moving) {
            // The top-level display_price is only what a session costs right now (often $0.00 for a
            // free first hour), so lead with the first tier and show the full schedule instead.
            const first = moving.tiers[0];
            const top = moving.dailyMax || moving.tiers[moving.tiers.length - 1].price;
            options.push({
                key: 'hourly',
                label: 'Hourly',
                price: first.price,
                unit: describeTierTime(first.time),
                moving,
                rangeLabel: first.price === top ? first.price : `${first.price} – ${top}`,
                terms,
                showHourlyNotice: true,
                bookingUrl: bookingUrl(spot, 'hourly'),
                infoUrl: infoUrl(spot, 'hourly'),
            });
        } else {
            options.push({
                key: 'hourly',
                label: isFlatRate ? 'Flat Rate' : 'Hourly',
                price: spot.display_price,
                unit: isFlatRate ? 'for the day' : 'per hour',
                note: !isFlatRate && spot.daily_max ? `${formatCents(spot.daily_max)} max daily` : undefined,
                terms: isFlatRate ? terms : undefined,
                showHourlyNotice: !isFlatRate,
                bookingUrl: bookingUrl(spot, 'hourly'),
                infoUrl: infoUrl(spot, 'hourly'),
            });
        }
    }
    if (spot.price_daily) {
        options.push({
            key: 'daily',
            label: 'Multi-Day',
            price: formatCents(spot.price_daily),
            unit: 'per day',
            showHourlyNotice: false,
            bookingUrl: bookingUrl(spot, 'daily'),
            infoUrl: infoUrl(spot, 'daily'),
        });
    }
    if (spot.price_monthly) {
        options.push({
            key: 'monthly',
            label: 'Monthly',
            price: formatCents(spot.price_monthly),
            unit: 'per month',
            showHourlyNotice: false,
            bookingUrl: bookingUrl(spot, 'monthly'),
            infoUrl: infoUrl(spot, 'monthly'),
        });
    }
    return options;
}

export function pickRateOption(options: RateOption[], preferredKey?: string | null): RateOption | undefined {
    return options.find((option) => option.key === preferredKey) || options[0];
}
